import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLazyQuery } from '@apollo/client';
import { Alert, Dialog, DialogContent, IconButton, Loading } from '@guildeducationinc/recess';
import FileCopyIcon from '@guildeducationinc/recess/icons/FileCopyIcon';
import FileDownloadOutlinedIcon from '@guildeducationinc/recess/icons/FileDownloadOutlinedIcon';
import { DOWNLOAD_REPORT } from '@src/graphql/queries/reportRun';
import { useReportRuns } from '@src/hooks/useReportRuns';
import { ReportStatus } from '@src/types';
import { DataTable, useLocale } from 'pe-core-frontend';
import { useState } from 'react';
export const ReportRuns = ({ clientId }) => {
    const { t, formatToLocalDatetime } = useLocale();
    const [reportError, setReportError] = useState(undefined);
    const { reportRuns, loading, error, page, pageSize, setPage, setPageSize, orderBy, setOrderBy, orderByDir, setOrderByDir, total, } = useReportRuns({
        clientId,
    });
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const handleShowErrors = (row) => {
        setReportError(row.error);
    };
    const [downloadReportQuery] = useLazyQuery(DOWNLOAD_REPORT);
    const handleDownloadReport = async (id) => {
        const response = await downloadReportQuery({ variables: { id } });
        if (response.data?.downloadReport) {
            window.open(response.data?.downloadReport);
        }
    };
    return (_jsxs(Loading, { label: 'ReportRuns', loaded: !loading, showChildrenWhileLoading: true, overlay: true, children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, rows: reportRuns || [], pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                    setPage(pagination.page + 1);
                    setPageSize(pagination.pageSize);
                }, rowCount: total, sortingMode: 'server', onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'id', sort: orderByDir }], columns: [
                    {
                        field: 'reportTemplateName',
                        headerName: t('Report Name'),
                        flex: 0.2,
                        sortable: true,
                    },
                    {
                        field: 'status',
                        headerName: t('Status'),
                        flex: 0.2,
                        sortable: true,
                        renderCell: ({ value }) => t(`ReportStatus.${value}`),
                    },
                    {
                        field: 'totalRows',
                        headerName: t('Total'),
                        flex: 0.1,
                        sortable: true,
                    },
                    {
                        field: 'startedAt',
                        headerName: t('Started'),
                        flex: 0.2,
                        sortable: true,
                        renderCell: ({ value }) => formatToLocalDatetime(value, { dateStyle: 'short', timeStyle: 'medium' }),
                    },
                    {
                        field: 'completedAt',
                        headerName: t('Completed'),
                        flex: 0.2,
                        sortable: true,
                        renderCell: ({ value }) => formatToLocalDatetime(value, { dateStyle: 'short', timeStyle: 'medium' }),
                    },
                    {
                        field: 'action',
                        headerName: t('Actions'),
                        renderCell: ({ row }) => (_jsxs("div", { children: [row.error && (_jsx(IconButton, { label: 'Edit', appearance: 'minimal', icon: _jsx(FileCopyIcon, { size: 20 }), onClick: () => handleShowErrors(row), className: 'margin--right_8' })), (row.status === ReportStatus.COMPLETED ||
                                    row.status === ReportStatus.COMPLETED_WITH_ERRORS) && (_jsx(IconButton, { label: t('Download'), appearance: 'minimal', icon: _jsx(FileDownloadOutlinedIcon, { size: 20 }), onClick: () => handleDownloadReport(row.id), className: 'margin--right_8' }))] })),
                        width: 100,
                        sortable: false,
                    },
                ] }), _jsx(Dialog, { isOpen: reportError !== undefined, title: t('Report Errors'), onClose: () => setReportError(undefined), size: 'xl', children: _jsx(DialogContent, { children: _jsx("pre", { className: 'text-sm border border-1 border-gray-700 bg-gray-300 p-3 overflow-auto', children: reportError }) }) })] }));
};
